<template>
    <div
        v-if="visible"
        class="ldw-root"
        role="dialog"
        aria-modal="true"
        :aria-label="ariaLabel">
        <div class="ldw-backdrop" @click="close"></div>

        <div class="ldw-widget">
            <div class="ldw-card">
                <button
                    type="button"
                    class="ldw-close"
                    :aria-label="$_locale('close')"
                    @click="close">
                    ×
                </button>

                <!-- LOGIN -->
                <template v-if="!authorized && view === 'login'">
                    <div class="ldw-header">
                        <span class="ldw-title">{{ $_locale('Login') }}</span>
                    </div>
                    <form class="ldw-form" @submit.prevent="login">
                        <div class="ldw-field">
                            <label class="ldw-label" for="ldw-username">{{ $_locale('Username') }}</label>
                            <input
                                id="ldw-username"
                                v-model="username"
                                type="text"
                                name="username"
                                class="ldw-input"
                                autocomplete="username"
                                required>
                        </div>
                        <div class="ldw-field">
                            <label class="ldw-label" for="ldw-password">{{ $_locale('Password') }}</label>
                            <input
                                id="ldw-password"
                                v-model="password"
                                type="password"
                                name="password"
                                class="ldw-input"
                                autocomplete="current-password"
                                required>
                        </div>
                        <button
                            type="submit"
                            class="ldw-btn ldw-btn-primary"
                            :disabled="loading">
                            {{ loading ? $_locale('Please wait') : $_locale('Login') }}
                        </button>
                        <a href="#" class="ldw-forgot" @click.prevent="view = 'forgot'">
                            {{ $_locale('Did you forget your password') }}?
                        </a>
                        <a v-if="registerUrl" :href="registerUrl" class="ldw-forgot">
                            {{ $_locale("Register as a webshop user") }}
                        </a>
                    </form>
                </template>

                <!-- FORGOT: give email -->
                <template v-else-if="view === 'forgot'">
                    <div class="ldw-header">
                        <span class="ldw-title">{{ $_locale('Password forgotten') }}?</span>
                    </div>
                    <form class="ldw-form" @submit.prevent="requestPasswordReset">
                        <div class="ldw-field">
                            <label class="ldw-label" for="ldw-email">{{ $_locale('Email') }}</label>
                            <input
                                id="ldw-email"
                                v-model="resetEmail"
                                type="email"
                                name="email"
                                class="ldw-input"
                                autocomplete="email"
                                required>
                        </div>
                        <button
                            type="submit"
                            class="ldw-btn ldw-btn-primary"
                            :disabled="loading">
                            {{ loading ? $_locale('Please wait') : $_locale('Request new password') }}
                        </button>
                        <a class="ldw-forgot" @click.prevent="view = 'login'">
                            ← {{ $_locale('Back to login') }}
                        </a>
                    </form>
                </template>

                <!-- FORGOT-SENT: confirmation message -->
                <template v-else-if="view === 'forgot-sent'">
                    <div class="ldw-header">
                        <span class="ldw-title">{{ $_locale('Check your email') }}</span>
                    </div>
                    <p class="ldw-info-text">
                        {{ $_locale('Password change link sent to the email address you provided') }}.
                    </p>
                    <button
                        type="button"
                        class="ldw-btn ldw-btn-secondary"
                        @click="view = 'login'">
                        {{ $_locale('Back to login') }}
                    </button>
                </template>

                <!-- RENEW: new password ticket -->
                <template v-else-if="view === 'renew'">
                    <div class="ldw-header">
                        <span class="ldw-title">{{ $_locale('Reset password') }}</span>
                    </div>
                    <form class="ldw-form" @submit.prevent="confirmNewPassword">
                        <div class="ldw-field">
                            <label class="ldw-label" for="ldw-pw1">{{ $_locale('New password') }}</label>
                            <input
                                id="ldw-pw1"
                                v-model="newPassword"
                                type="password"
                                class="ldw-input"
                                autocomplete="new-password"
                                required>
                        </div>
                        <div class="ldw-field">
                            <label class="ldw-label" for="ldw-pw2">{{ $_locale('Retype password') }}</label>
                            <input
                                id="ldw-pw2"
                                v-model="newPasswordConfirm"
                                type="password"
                                class="ldw-input"
                                autocomplete="new-password"
                                required>
                        </div>
                        <button
                            type="submit"
                            class="ldw-btn ldw-btn-primary"
                            :disabled="loading">
                            {{ loading ? $_locale('Please wait') : $_locale('Set new password') }}
                        </button>
                    </form>
                </template>

                <!-- AUTHORIZED -->
                <template v-else-if="authorized">
                    <div class="ldw-header">
                        <span class="ldw-title">{{ $_locale('My account') }}</span>
                    </div>
                    <div class="ldw-actions">
                        <a :href="ordersUrl" class="ldw-btn ldw-btn-secondary">
                            {{ $_locale('My orders') }}
                        </a>
                        <button
                            type="button"
                            class="ldw-btn ldw-btn-primary"
                            :disabled="loading"
                            @click="logout">
                            {{ loading ? $_locale('Please wait') : $_locale('Logout') }}
                        </button>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { on } from '../common/events.js'

export default {
    name: 'LoginDrawer',

    data() {
        return {
            visible: false,
            authorized: false,
            view: 'login',       // 'login' | 'forgot' | 'forgot-sent' | 'renew'
            ordersUrl: '',
            registerUrl: '',
            username: '',
            password: '',
            resetEmail: '',
            newPassword: '',
            newPasswordConfirm: '',
            resetTicket: null,
            loading: false,
        };
    },

    computed: {
        ariaLabel() {
            return this.authorized ? this.$_locale('My account') : this.$_locale('Login');
        },
    },

    mounted() {
        this._unsubscribeDrawer = on(document.body, 'wisenetwork:openLoginDrawer', this.open);


        const params = new URLSearchParams(window.location.search);
        const ticket = params.get('resetTicket');

        if (ticket) {
            this.resetTicket = ticket;
            this.view = 'renew';
            this.visible = true;
            const url = new URL(window.location.href);
            url.searchParams.delete('resetTicket');
            window.history.replaceState({}, '', url);
        }
    },

    beforeDestroy() {
        if (this._unsubscribeDrawer) {
            this._unsubscribeDrawer();
        }
    },

    methods: {
        open(evt, data) {
            this.authorized = data?.authorized ?? false;
            this.ordersUrl = data?.ordersUrl ?? '';
            this.registerUrl = data?.registerUrl ?? '';
            this.username = '';
            this.password = '';
            this.resetEmail = '';
            this.newPassword = '';
            this.newPasswordConfirm = '';
            this.loading = false;
            this.view = 'login';
            this.visible = true;
        },

        close() {
            this.visible = false;
        },

        async login() {
            this.loading = true;
            const params = new URLSearchParams({
                username: this.username,
                password: this.password,
            });
            try {
                await this.$_http.post('/api/1.0/auth/', params);
                window.location.reload();
            } catch (error) {
                this.loading = false;
                this.$_swal({
                    title: error?.response?.data?.error || this.$_locale('Login failed'),
                    icon: 'error',
                    confirmButtonText: this.$_locale('OK'),
                });
            }
        },

        async logout() {
            this.loading = true;
            try {
                await this.$_http.post('/api/1.0/logout/');
                window.location.href = '/';
            } catch (error) {
                this.loading = false;
                this.$_swal({
                    title: error?.response?.data?.error || this.$_locale('Logout failed'),
                    icon: 'error',
                    confirmButtonText: this.$_locale('OK'),
                });
            }
        },

        async requestPasswordReset() {
            this.loading = true;
            const params = new URLSearchParams({ email: this.resetEmail });
            try {
                await this.$_http.post('/api/1.0/auth/forgot/', params);
                this.view = 'forgot-sent';
            } catch (error) {
                this.$_swal({
                    title: error?.response?.data?.error || this.$_locale('Request failed'),
                    icon: 'error',
                    confirmButtonText: this.$_locale('OK'),
                });
            } finally {
                this.loading = false;
            }
        },

        async confirmNewPassword() {
            if (this.newPassword !== this.newPasswordConfirm) {
                this.$_swal({
                    title: this.$_locale('Passwords do not match'),
                    icon: 'error',
                    confirmButtonText: this.$_locale('OK'),
                });
                return;
            }
            this.loading = true;
            const params = new URLSearchParams({
                ticket: this.resetTicket,
                password: this.newPassword,
            });
            try {
                await this.$_http.post('/api/1.0/auth/renewpassword/', params);
                this.$_swal({
                    title: this.$_locale('Password updated'),
                    icon: 'success',
                    confirmButtonText: this.$_locale('OK'),
                }).then(() => {
                    this.view = 'login';
                    this.resetTicket = null;
                });
            } catch (error) {
                this.$_swal({
                    title: error?.response?.data?.error || this.$_locale('Password reset failed'),
                    icon: 'error',
                    confirmButtonText: this.$_locale('OK'),
                });
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style>

.ldw-forgot {
    display: block;
    text-align: center;
    font-size: 13px;
    color: #888888;
    text-decoration: none;
    margin-top: 2px;
    cursor: pointer;
}

.ldw-info-text {
    font-size: 15px;
    color: #555555;
    margin: 0 0 20px;
    line-height: 1.5;
}

.ldw-forgot:hover {
    color: #111111;
    text-decoration: underline;
}

.ldw-root {
    position: fixed;
    inset: 0;
    z-index: 900;
    pointer-events: none;
}

.ldw-backdrop {
    position: absolute;
    inset: 0;
    background: rgba(17, 17, 17, 0.22);
    backdrop-filter: blur(2px);
    pointer-events: auto;
}

.ldw-widget {
    position: absolute;
    right: 20px;
    bottom: 20px;
    width: 420px;
    max-width: calc(100vw - 40px);
    pointer-events: auto;
}

.ldw-card {
    position: relative;
    background: #ffffff;
    color: #111111;
    border-radius: 24px;
    box-shadow:
        0 24px 60px rgba(0, 0, 0, 0.18),
        0 8px 24px rgba(0, 0, 0, 0.10);
    overflow: hidden;
    padding: 24px;
}

.ldw-close {
    position: absolute;
    top: 14px;
    right: 14px;
    width: 38px;
    height: 38px;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: #111111;
    font-size: 26px;
    line-height: 1;
    cursor: pointer;
}

.ldw-header {
    padding-right: 44px;
    margin-bottom: 20px;
}

.ldw-title {
    font-size: 20px;
    font-weight: 600;
    line-height: 1.3;
    color: #111111;
}

.ldw-form {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.ldw-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.ldw-label {
    font-size: 14px;
    font-weight: 500;
    color: #111111;
}

.ldw-input {
    border: 1px solid #cfcfcf;
    border-radius: 10px;
    padding: 10px 14px;
    font-size: 15px;
    color: #111111;
    outline: none;
    width: 100%;
    box-sizing: border-box;
}

.ldw-input:focus {
    border-color: #111111;
}

.ldw-actions {
    display: grid;
    gap: 10px;
}

.ldw-btn {
    appearance: none;
    border: 1px solid transparent;
    border-radius: 999px;
    min-height: 52px;
    padding: 12px 18px;
    font-size: 16px;
    font-weight: 600;
    text-align: center;
    cursor: pointer;
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 6px;
}

.ldw-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.ldw-btn-primary {
    background: #111111;
    color: #ffffff;
    border-color: #111111;
}

.ldw-btn-secondary {
    background: #ffffff;
    color: #111111;
    border-color: #cfcfcf;
}

@media (max-width: 640px) {
    .ldw-widget {
        right: 20px;
        left: 20px;
        bottom: 20px;
        width: auto;
        max-width: none;
    }

    .ldw-card {
        padding: 20px 16px 16px;
    }
}
</style>
